import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Image as ImageIcon, File } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { FileViewer } from './FileViewer';

export function DocumentGallery() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);

  useEffect(() => {
    fetchDocuments();
    fetchCaseData();
  }, [id]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('case_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseData = async () => {
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('reference_number, not_at_fault_name')
        .eq('id', id)
        .single();

      if (error) throw error;
      setCaseData(data);
    } catch (error) {
      console.error('Error fetching case:', error);
    }
  };

  const handleFileDelete = (documentId) => {
    setDocuments(prev => prev.filter(doc => doc.id !== documentId));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/cases/${id}`)}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Case
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {caseData?.reference_number || 'Case Documents'}
            </h1>
            <p className="text-gray-600">{caseData?.not_at_fault_name}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {documents.map(doc => (
          <div key={doc.id} className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {doc.metadata?.type?.startsWith('image/') ? (
                  <ImageIcon className="h-5 w-5 text-indigo-500" />
                ) : doc.metadata?.type?.includes('pdf') ? (
                  <FileText className="h-5 w-5 text-red-500" />
                ) : (
                  <File className="h-5 w-5 text-gray-500" />
                )}
                <span className="font-medium text-gray-700 truncate max-w-[200px]">
                  {doc.filename}
                </span>
              </div>
              <span className="text-xs text-gray-500">
                {new Date(doc.created_at).toLocaleDateString()}
              </span>
            </div>
            <div className="p-4">
              <FileViewer file={doc} onDelete={handleFileDelete} />
            </div>
          </div>
        ))}

        {documents.length === 0 && (
          <div className="col-span-full text-center py-12 bg-white rounded-xl shadow-md">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
            <p className="text-gray-500">Upload documents to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}